import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiClipboard, FiCopy, FiX } from "react-icons/fi";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { useOnboardingFlow } from "../OnboardingFlowContext";
import { getOnboardingMessages } from "../onboardingI18n";
import { getRoomsMessages } from "../roomsMessages";
import {
  buildRowsPayload,
  formatDate,
  normalizeIncomingRows,
  normalizeOptionalText,
  pickCopyableData,
  rowsHaveRequiredRoomNumbers,
} from "../roomsDraftShared";
import "../OnboardingApp.css";

export default function RoomsGridStep() {
  const { languageId } = usePmsTheme();
  const {
    clearStepCommandState,
    markStepComplete,
    markStepIncomplete,
    setStepCommandState,
    steps,
  } = useOnboardingFlow();
  const onboardingCopy = getOnboardingMessages(languageId);
  const copy = getRoomsMessages(languageId).grid;
  const { saveError, saveSuccess, validationDuplicate } = copy;
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [sessionMeta, setSessionMeta] = useState(null);
  const [clipboard, setClipboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [textModal, setTextModal] = useState(null); // { clientId, field, label, value }
  const textModalRef = useRef(null);
  const stepNumber = useMemo(
    () => Math.max(steps.findIndex((step) => step.id === "rooms-grid") + 1, 1),
    [steps]
  );

  const loadDraft = useCallback(async () => {
    setLoading(true);
    setFeedback({ type: "", message: "" });

    try {
      const response = await axios.get("/api/onboarding/rooms");
      const incomingCategories = Array.isArray(response.data?.categories) ? response.data.categories : [];
      const incomingRows = normalizeIncomingRows(response.data?.rows || []);

      setCategories(incomingCategories);
      setRows(incomingRows);
      setSessionMeta(response.data?.session || null);
      setIsDirty(false);

      if (incomingRows.length > 0) {
        markStepComplete("rooms-counts");
      }

      if (rowsHaveRequiredRoomNumbers(incomingRows)) {
        markStepComplete("rooms-grid");
      } else {
        markStepIncomplete("rooms-grid");
      }
    } catch (error) {
      console.error("[Onboarding][RoomsGrid] Error loading draft:", error);
      setCategories([]);
      setRows([]);
      setSessionMeta(null);
      markStepIncomplete("rooms-grid");
      setFeedback({
        type: "error",
        message: copy.loadError,
      });
    } finally {
      setLoading(false);
    }
  }, [copy.loadError, markStepComplete, markStepIncomplete]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  useEffect(() => {
    if (!rowsHaveRequiredRoomNumbers(rows)) {
      markStepIncomplete("rooms-grid");
    }
  }, [markStepIncomplete, rows]);

  const groupedRows = useMemo(
    () =>
      categories.map((category) => ({
        category,
        rows: rows.filter((row) => Number(row.onboarding_room_category_id) === Number(category.id)),
      })),
    [categories, rows]
  );

  const categoriesWithRooms = useMemo(
    () => groupedRows.filter((group) => group.rows.length > 0).length,
    [groupedRows]
  );

  const updateRow = (clientId, field, value) => {
    setIsDirty(true);
    setRows((currentRows) =>
      currentRows.map((row) => (row.client_id === clientId ? { ...row, [field]: value } : row))
    );
  };

  const handleCopy = (row) => {
    setClipboard({
      room_number: normalizeOptionalText(row.room_number),
      data: pickCopyableData(row),
    });
    setFeedback({
      type: "success",
      message: copy.clipboardCopied,
    });
  };

  const handlePaste = (clientId) => {
    if (!clipboard?.data) {
      setFeedback({
        type: "error",
        message: copy.clipboardEmpty,
      });
      return;
    }

    setIsDirty(true);
    setRows((currentRows) =>
      currentRows.map((row) =>
        row.client_id === clientId
          ? {
              ...row,
              ...clipboard.data,
            }
          : row
      )
    );
    setFeedback({
      type: "success",
      message: copy.clipboardPasted,
    });
  };

  const validateBeforeSave = useCallback(() => {
    const seenRoomNumbers = new Set();

    for (const row of rows) {
      const roomNumber = normalizeOptionalText(row.room_number);

      if (!roomNumber) {
        continue;
      }

      const roomNumberKey = roomNumber.toLowerCase();

      if (seenRoomNumbers.has(roomNumberKey)) {
        return validationDuplicate(roomNumber);
      }

      seenRoomNumbers.add(roomNumberKey);
    }

    return null;
  }, [rows, validationDuplicate]);

  const handleSave = useCallback(async () => {
    const validationError = validateBeforeSave();

    if (validationError) {
      setFeedback({ type: "error", message: validationError });
      return false;
    }

    setSaving(true);
    setFeedback({ type: "", message: "" });

    try {
      const response = await axios.put("/api/onboarding/rooms", {
        rows: buildRowsPayload(rows),
      });
      const savedRows = normalizeIncomingRows(response.data?.rows || []);

      setRows(savedRows);
      setSessionMeta(response.data?.session || null);
      setIsDirty(false);
      markStepComplete("rooms-counts");

      if (rowsHaveRequiredRoomNumbers(savedRows)) {
        markStepComplete("rooms-grid");
      } else {
        markStepIncomplete("rooms-grid");
      }

      setFeedback({
        type: "success",
        message: saveSuccess,
      });
      return true;
    } catch (error) {
      console.error("[Onboarding][RoomsGrid] Error saving grid:", error);
      setFeedback({
        type: "error",
        message: error?.response?.data?.error || saveError,
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    markStepComplete,
    markStepIncomplete,
    rows,
    saveError,
    saveSuccess,
    validateBeforeSave,
  ]);

  useEffect(() => {
    setStepCommandState("rooms-grid", {
      onSave: handleSave,
      canSave: !loading,
      canProceed: !isDirty,
      isDirty,
      isSaving: saving,
    });

    return () => {
      clearStepCommandState("rooms-grid");
    };
  }, [clearStepCommandState, handleSave, isDirty, loading, saving, setStepCommandState]);

  const openTextModal = (clientId, field, label, value, initialChar = null) => {
    const initial = initialChar !== null ? value + initialChar : value;
    setTextModal({ clientId, field, label, value: initial });
  };

  const handleTextCellKeyDown = (e, clientId, field, label, value) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openTextModal(clientId, field, label, value);
    } else if (e.key.length === 1) {
      e.preventDefault();
      openTextModal(clientId, field, label, value, e.key);
    } else if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      openTextModal(clientId, field, label, value);
    }
  };

  const confirmTextModal = () => {
    if (!textModal) return;
    updateRow(textModal.clientId, textModal.field, textModal.value);
    setTextModal(null);
  };

  const closeTextModal = () => setTextModal(null);

  if (loading) {
    return (
      <div className="onboarding-emptyState">
        <h3 className="onboarding-sectionTitle">{copy.title}</h3>
        <p className="onboarding-sectionText">{copy.copy}</p>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="onboarding-emptyState onboarding-formCard--centered">
        <div className="onboarding-eyebrow">{copy.eyebrow}</div>
        <h3 className="onboarding-sectionTitle">{copy.emptyTitle}</h3>
        <p className="onboarding-sectionText">{copy.emptyCopy}</p>
      </div>
    );
  }

  return (
    <>
    <div className="onboarding-grid">
      <section className="onboarding-formCard">
        <div className="onboarding-formHeader">
          <div>
            <div className="onboarding-eyebrow">{copy.eyebrow}</div>
            <div className="onboarding-stepCount">{onboardingCopy.progress.stepCounter(stepNumber, steps.length)}</div>
            <h3 className="onboarding-sectionTitle">{copy.title}</h3>
            <p className="onboarding-sectionText">{copy.copy}</p>
          </div>
        </div>

        <div className="onboarding-roomTableStack">
          {groupedRows.map(({ category, rows: categoryRows }) =>
            categoryRows.length ? (
              <section key={category.id} className="onboarding-roomCategoryCard">
                <div className="onboarding-roomTableHeader">
                  <div>
                    <h4>{category.name}</h4>
                    {category.description ? <p className="onboarding-hint">{category.description}</p> : null}
                  </div>
                  <span className="onboarding-stepState">{categoryRows.length}</span>
                </div>

                <div className="onboarding-roomTableWrap">
                  <table className="onboarding-roomTable">
                    <thead>
                      <tr>
                        <th>{copy.roomNumber}</th>
                        <th>{copy.floor}</th>
                        <th>{copy.sqMeters}</th>
                        <th>{copy.numBeds}</th>
                        <th>{copy.bedType}</th>
                        <th>{copy.view}</th>
                        <th>{copy.features}</th>
                        <th>{copy.description}</th>
                        <th>{copy.copyRow}</th>
                        <th>{copy.pasteRow}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryRows.map((row, index) => (
                        <tr key={row.client_id}>
                          <td>
                            <input
                              className="onboarding-tableInput"
                              value={row.room_number}
                              onChange={(event) => updateRow(row.client_id, "room_number", event.target.value)}
                              placeholder={copy.roomNumberPlaceholder || `${index + 1}`}
                            />
                          </td>
                          <td>
                            <input
                              className="onboarding-tableInput"
                              type="number"
                              value={row.floor}
                              onChange={(event) => updateRow(row.client_id, "floor", event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              className="onboarding-tableInput"
                              type="number"
                              min="0"
                              step="0.1"
                              value={row.sq_meters}
                              onChange={(event) => updateRow(row.client_id, "sq_meters", event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              className="onboarding-tableInput"
                              type="number"
                              min="0"
                              value={row.num_beds}
                              onChange={(event) => updateRow(row.client_id, "num_beds", event.target.value)}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className={`onboarding-textCell ${row.bed_type ? "onboarding-textCell--filled" : ""}`}
                              title={row.bed_type || undefined}
                              onClick={() => openTextModal(row.client_id, "bed_type", copy.bedType, row.bed_type)}
                              onKeyDown={(e) => handleTextCellKeyDown(e, row.client_id, "bed_type", copy.bedType, row.bed_type)}
                            >
                              {row.bed_type || <span className="onboarding-textCell__placeholder">{copy.bedTypePlaceholder}</span>}
                            </button>
                          </td>
                          <td>
                            <button
                              type="button"
                              className={`onboarding-textCell ${row.view ? "onboarding-textCell--filled" : ""}`}
                              title={row.view || undefined}
                              onClick={() => openTextModal(row.client_id, "view", copy.view, row.view)}
                              onKeyDown={(e) => handleTextCellKeyDown(e, row.client_id, "view", copy.view, row.view)}
                            >
                              {row.view || <span className="onboarding-textCell__placeholder">{copy.viewPlaceholder}</span>}
                            </button>
                          </td>
                          <td>
                            <button
                              type="button"
                              className={`onboarding-textCell ${row.features ? "onboarding-textCell--filled" : ""}`}
                              title={row.features || undefined}
                              onClick={() => openTextModal(row.client_id, "features", copy.features, row.features)}
                              onKeyDown={(e) => handleTextCellKeyDown(e, row.client_id, "features", copy.features, row.features)}
                            >
                              {row.features || <span className="onboarding-textCell__placeholder">{copy.featuresPlaceholder}</span>}
                            </button>
                          </td>
                          <td>
                            <button
                              type="button"
                              className={`onboarding-textCell ${row.description ? "onboarding-textCell--filled" : ""}`}
                              title={row.description || undefined}
                              onClick={() => openTextModal(row.client_id, "description", copy.description, row.description)}
                              onKeyDown={(e) => handleTextCellKeyDown(e, row.client_id, "description", copy.description, row.description)}
                            >
                              {row.description || <span className="onboarding-textCell__placeholder">{copy.descriptionPlaceholder}</span>}
                            </button>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="onboarding-tableAction"
                              onClick={() => handleCopy(row)}
                              title={copy.copyRow}
                              aria-label={copy.copyRow}
                            >
                              <FiCopy size={15} />
                            </button>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="onboarding-tableAction"
                              onClick={() => handlePaste(row.client_id)}
                              title={copy.pasteRow}
                              aria-label={copy.pasteRow}
                            >
                              <FiClipboard size={15} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null
          )}
        </div>

        {feedback.message ? (
          <div
            className={`onboarding-status ${
              feedback.type === "success" ? "onboarding-status--success" : "onboarding-status--error"
            }`}
          >
            {feedback.message}
          </div>
        ) : null}
      </section>

      <aside className="onboarding-infoCard">
        <div className="onboarding-eyebrow">{copy.eyebrow}</div>
        <h3 className="onboarding-sectionTitle">{copy.summaryTitle}</h3>
        <p className="onboarding-sectionText">{copy.summaryIntro}</p>

        <div className="onboarding-pointsList">
          {copy.summaryPoints.map((point) => (
            <div key={point} className="onboarding-pointItem">
              <span className="onboarding-pointDot" />
              <span>{point}</span>
            </div>
          ))}
        </div>

        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryTotalLabel}</span>
          <span className="onboarding-kpiValue">{rows.length}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summaryCategoriesLabel}</span>
          <span className="onboarding-kpiValue">{categoriesWithRooms}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.copyHint}</span>
          <span className="onboarding-kpiValue">{clipboard?.room_number || copy.copyEmpty}</span>
        </div>
        <div className="onboarding-kpi">
          <span className="onboarding-kpiLabel">{copy.summarySavedLabel}</span>
          <span className="onboarding-kpiValue">
            {sessionMeta?.updated_at ? formatDate(sessionMeta.updated_at) : copy.summaryNotSaved}
          </span>
        </div>
      </aside>
    </div>

    {textModal ? (
      <div className="onboarding-textModal__backdrop" onClick={confirmTextModal}>
        <div
          className="onboarding-textModal"
          ref={textModalRef}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="onboarding-textModal__header">
            <span className="onboarding-textModal__label">{textModal.label}</span>
          </div>
          <textarea
            className="onboarding-textModal__textarea"
            value={textModal.value}
            onChange={(e) => setTextModal((prev) => ({ ...prev, value: e.target.value }))}
            rows={5}
            ref={(el) => {
              if (el) {
                el.focus();
                el.setSelectionRange(el.value.length, el.value.length);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") closeTextModal();
            }}
          />
          <div className="onboarding-textModal__footer">
            <button type="button" className="onboarding-secondaryBtn" onClick={closeTextModal}>
              <FiX size={14} />
              Cancel
            </button>
            <button type="button" className="onboarding-primaryBtn" onClick={confirmTextModal}>
              <FiCheck size={14} />
              OK
            </button>
          </div>
        </div>
      </div>
    ) : null}
    </>
  );
}
